// src/types/index.ts
export enum OrderStatus {
    PENDING = 'PENDING',
    CODE_RECEIVED = 'CODE_RECEIVED',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR',
    CANCELED = 'CANCELED'
}

export interface Order {
    _id: string;
    code: string;
    userId: string;
    username: string;
    createdBy?: string;
    note?: string;
    status: OrderStatus;
    integrationPriority?: boolean;
    progress: number;
    totalSteps: number;
    price?: number;
    paidAmount?: number;
    unpaidAmount?: number;
    currentStep: number;
    processedSteps?: number;
    successfulSteps?: number;
    failedSteps?: number;
    stepErrors?: Array<{ step?: number; count: number; reason: string; createdAt: Date }>;
    message?: string;
    error?: string;
    redoRequestStatus?: 'pending' | 'approved';
    redoRequestReason?: string;
    redoRequestMessageId?: string;
    redoOrderId?: string;
    refundRequestStatus?: 'pending' | 'processing' | 'approved';
    refundRequestReason?: string;
    refundRequestMessageId?: string;
    refundAmount?: number;
    walletCharged?: boolean;
    customerConfirmed?: boolean;
    customerConfirmedAt?: Date;
    startedAt?: Date;
    createdAt: Date;
    updatedAt: Date;
}

export type MessageStatus = 'sent' | 'delivered' | 'read';

export interface MessageReplyPreview {
    _id?: string;
    content: string;
    senderUsername?: string;
    senderFullName?: string;
    type?: Message['type'];
    isDeleted?: boolean;
}

export interface MessageReaction {
    userId: string;
    emoji: string;
}

export interface Message {
    id: string;
    _id?: string; // MongoDB ID for message updates
    userId: string;
    username: string;
    content: string;
    type: 'text' | 'image' | 'file' | 'order' | 'system';
    orderId?: string;
    imageUrl?: string;
    fileUrl?: string;
    fileName?: string;
    replyTo?: string | MessageReplyPreview;
    timestamp: Date;
    isAdmin: boolean;
    status?: MessageStatus;
    isRead?: boolean;
    reactions?: MessageReaction[];
    // Order progress tracking
    progress?: number;
    currentStep?: number;
    totalSteps?: number;
    orderStatus?: OrderStatus;
}

export interface ChatRoom {
    id: string;
    userId: string;
    username: string;
    lastMessage?: Message;
    unread: number;
    online: boolean;
}

export interface User {
    _id: string;
    username: string;
    fullName: string;
    email?: string;
    phone?: string;
    role: 'admin' | 'partner' | 'user' | 'staff';
    avatar?: string;
    status?: 'active' | 'inactive';
    lastLogin?: Date;
    createdAt: Date;
    updatedAt: Date;
}

export interface AuthResponse {
    accessToken: string;
    refreshToken: string;
    user: User | null;
}

export interface LoginRequest {
    username: string;
    password: string;
    captchaToken: string;
}

export interface RegisterRequest {
    username: string;
    password: string;
    captchaToken: string;
    fullName?: string;
    email?: string;
    phone?: string;
}

export interface AuthState {
    isAuthenticated: boolean;
    user: User | null;
    accessToken: string | null;
    refreshToken: string | null;
    isLoading: boolean;
    error: string | null;
}